import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import LandingNavbar from '../landing/LandingNavbar';
import LandingFooter from '../landing/LandingFooter';
import './LegalPageLayout.css';

/**
 * Parse markdown-style bold (**text**) into HTML
 */
function parseBold(text) {
  return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
}

/**
 * Render a markdown-style table
 */
function renderTable(text, key) {
  const rows = text.split('\n').filter((r) => r.trim());
  if (rows.length < 2) return null;

  const headers = rows[0].split('|').map((h) => h.trim()).filter(Boolean);
  const dataRows = rows.slice(2); // skip header separator row

  return (
    <div key={key} style={{ overflowX: 'auto', margin: '16px 0' }}>
      <table>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rIdx) => {
            const cells = row.split('|').map((c) => c.trim()).filter(Boolean);
            return (
              <tr key={rIdx}>
                {cells.map((cell, cIdx) => (
                  <td key={cIdx} dangerouslySetInnerHTML={{ __html: parseBold(cell) }} />
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Render section content — handles paragraphs, bullet lists, tables, bold
 */
function renderContent(content) {
  if (!content) return null;

  const paragraphs = content.split('\n\n');

  return paragraphs.map((paragraph, pIdx) => {
    // Table detection
    if (paragraph.includes('|') && paragraph.includes('---')) {
      return renderTable(paragraph, pIdx);
    }

    // Bullet list detection
    const lines = paragraph.split('\n');
    const bulletLines = lines.filter((l) => l.startsWith('• ') || l.startsWith('- '));
    if (bulletLines.length > 0 && bulletLines.length === lines.filter((l) => l.trim()).length) {
      return (
        <ul key={pIdx}>
          {bulletLines.map((item, i) => (
            <li
              key={i}
              dangerouslySetInnerHTML={{
                __html: parseBold(item.replace(/^[•\-]\s*/, '')),
              }}
            />
          ))}
        </ul>
      );
    }

    // Regular paragraph
    return (
      <p
        key={pIdx}
        dangerouslySetInnerHTML={{
          __html: parseBold(paragraph.replace(/\n/g, '<br/>')),
        }}
      />
    );
  });
}

/**
 * Sibling legal pages for cross-navigation
 */
const LEGAL_PAGES = [
  { type: 'privacy-policy', label: 'Privacy Policy', path: '/legal/privacy-policy' },
  { type: 'terms-of-service', label: 'Terms of Service', path: '/legal/terms-of-service' },
  { type: 'cookie-policy', label: 'Cookie Policy', path: '/legal/cookie-policy' },
];

/**
 * LegalPageLayout — Shared layout for Privacy Policy, Terms, Cookie Policy
 * Renders data fetched from backend. No dummy data.
 */
const LegalPageLayout = ({ page, loading, error, onRetry }) => {
  const location = useLocation();

  // Sort sections by order field
  const sortedSections = useMemo(() => {
    if (!page?.sections) return [];
    return [...page.sections].sort((a, b) => a.order - b.order);
  }, [page?.sections]);

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // ── Loading ──
  if (loading) {
    return (
      <div className="legal-page">
        <LandingNavbar />
        <div className="legal-page__container">
          <div className="legal-page__loading">
            <div className="legal-page__spinner" />
            <span className="legal-page__loading-text">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ──
  if (error || !page) {
    return (
      <div className="legal-page">
        <LandingNavbar />
        <div className="legal-page__container">
          <div className="legal-page__error">
            <div className="legal-page__error-icon">
              <AlertCircle size={24} />
            </div>
            <h2 className="legal-page__error-title">Unable to load page</h2>
            <p className="legal-page__error-msg">
              {error || 'This legal page is currently unavailable. Please try again later.'}
            </p>
            {onRetry && (
              <button className="legal-page__error-btn" onClick={onRetry}>
                Try Again
              </button>
            )}
          </div>
        </div>
        <LandingFooter />
      </div>
    );
  }

  return (
    <div className="legal-page">
      <LandingNavbar />

      <div className="legal-page__container">
        {/* Back link */}
        <Link to="/" className="legal-page__back">
          <ArrowLeft size={14} />
          Back to home
        </Link>

        {/* Header */}
        <header className="legal-page__header">
          <h1 className="legal-page__title">{page.title}</h1>
          <div className="legal-page__meta">
            <span>Effective {formatDate(page.effectiveDate)}</span>
            <span className="legal-page__meta-divider" />
            <span>Version {page.version}</span>
            {page.metadata?.companyName && (
              <>
                <span className="legal-page__meta-divider" />
                <span>{page.metadata.companyName}</span>
              </>
            )}
          </div>
        </header>

        {/* Table of Contents */}
        {sortedSections.length > 3 && (
          <nav className="legal-page__toc">
            <h2 className="legal-page__toc-title">Table of Contents</h2>
            <ol className="legal-page__toc-list">
              {sortedSections.map((section, idx) => (
                <li key={section._id} className="legal-page__toc-item">
                  <a href={`#section-${idx}`} className="legal-page__toc-link">
                    <span className="legal-page__toc-number">{idx + 1}.</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Top-level content (if any) */}
        {page.content && (
          <div className="legal-page__top-content legal-page__content">
            {renderContent(page.content)}
          </div>
        )}

        {/* Sections */}
        <div className="legal-page__sections">
          {sortedSections.map((section, idx) => (
            <section
              key={section._id}
              id={`section-${idx}`}
              className="legal-page__section"
            >
              <h2 className="legal-page__section-title">
                <span className="legal-page__section-number">{idx + 1}.</span>
                {section.title}
              </h2>
              <div className="legal-page__content">
                {renderContent(section.content)}
              </div>
            </section>
          ))}
        </div>

        {/* Cross-navigation to other legal pages */}
        <nav className="legal-page__nav">
          <h3 className="legal-page__nav-title">Other Legal Pages</h3>
          <div className="legal-page__nav-links">
            {LEGAL_PAGES.map((lp) => (
              <Link
                key={lp.type}
                to={lp.path}
                className={`legal-page__nav-link ${
                  location.pathname === lp.path ? 'legal-page__nav-link--active' : ''
                }`}
              >
                {lp.label}
              </Link>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <footer className="legal-page__footer">
          <p className="legal-page__footer-text">
            Last updated: {formatDate(page.updatedAt)}
            {page.metadata?.companyName && ` — ${page.metadata.companyName}`}
          </p>
          {page.metadata?.contactEmail && (
            <p className="legal-page__footer-text">
              Questions? Contact us at{' '}
              <a
                href={`mailto:${page.metadata.contactEmail}`}
                className="legal-page__footer-email"
              >
                {page.metadata.contactEmail}
              </a>
            </p>
          )}
        </footer>
      </div>

      <LandingFooter />
    </div>
  );
};

export default LegalPageLayout;
